"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useModal } from "@/hook/modal";
import { getAgencyArea, getAgencyAreaDetails } from "@/api/agency";
import { useSelectedPathStore } from "@/store/selectedPathStore";

interface UseAreaSelectionOptions {
  clearCoverageStorageOnMount?: boolean;
}

export function useAreaSelection(options: UseAreaSelectionOptions = {}) {
  const { clearCoverageStorageOnMount = false } = options;

  const [chipData, setChipData] = useState<any[]>([]);
  const [provinceData, setProvinceData] = useState<any>(null);
  const [pathData, setPathData] = useState<any[]>([]);
  const [areaDetails, setAreaDetails] = useState<any[]>([]);
  const [stepCounter, setStepCounter] = useState(0);
  const [provinceLoading, setProvinceLoading] = useState(false);
  const [selectedRadioId, setSelectedRadioId] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [checkRadioRequestToSendarea_id, setCheckRadioRequestToSendarea_id] =
    useState(true);
  const [currentParentId, setCurrentParentId] = useState<number | null>(null);

  const { selectedpath, setSelectedPath } = useSelectedPathStore();

  const {
    isModalOpen: areaModalOpen,
    handleOpen: openAreaModal,
    handleClose: handleAreaModalClose,
  } = useModal();

  const resetAreaWizard = useCallback(() => {
    setCurrentParentId(null);
    setChipData([]);
    setPathData([]);
    setStepCounter(0);
    setSelectedRadioId(null);
    setCheckRadioRequestToSendarea_id(true);
    setAreaDetails([]);
  }, []);

  const handleAreaModalOpen = useCallback(() => {
    resetAreaWizard();
    openAreaModal();
  }, [openAreaModal, resetAreaWizard]);

  useEffect(() => {
    if (clearCoverageStorageOnMount) {
      localStorage.removeItem("coverage_area_id");
    }
    setSelectedPath([]);
  }, [clearCoverageStorageOnMount, setSelectedPath]);

  useEffect(() => {
    if (!areaModalOpen || provinceData != null) return;

    let isMounted = true;

    const loadProvinces = async () => {
      try {
        setProvinceLoading(true);
        const data = await getAgencyArea();
        if (isMounted) setProvinceData(data);
      } catch (error) {
        console.error("Failed to load provinces:", error);
      } finally {
        if (isMounted) setProvinceLoading(false);
      }
    };

    loadProvinces();

    return () => {
      isMounted = false;
    };
  }, [areaModalOpen, provinceData]);

  useEffect(() => {
    if (currentParentId === null) {
      return;
    }

    let isMounted = true;

    const loadDetails = async () => {
      try {
        setDetailsLoading(true);
        const data = await getAgencyAreaDetails(currentParentId);
        if (isMounted) setAreaDetails(data ?? []);
      } catch (error) {
        console.error(
          `Failed to load details for id ${currentParentId}:`,
          error,
        );
        if (isMounted) setAreaDetails([]);
      } finally {
        if (isMounted) setDetailsLoading(false);
      }
    };

    loadDetails();

    return () => {
      isMounted = false;
    };
  }, [currentParentId]);

  const selectedAreaText = useMemo(() => {
    if (!selectedpath?.length) {
      return "انتخاب منطقه تحت پوشش";
    }
    return selectedpath
      .map((item: any) => item.name || item.area_name)
      .join(" ← ");
  }, [selectedpath]);

  const selectedAreaId = useMemo(() => {
    if (!selectedpath?.length) {
      return "";
    }
    return selectedpath
      .map((item: any) => item.area_id ?? item.area_id)
      .join(" ← ");
  }, [selectedpath]);

  const handleSettingChipData = (value: any) => {
    setChipData((prev) => [
      ...prev,
      {
        name: value?.name ?? value?.area_name,
        id: value?.area_id ?? value?.id,
        step: stepCounter,
      },
    ]);
  };

  const handleBack = (clickType = "modal") => {
    if (clickType === "cmp" && stepCounter === 0) {
      setPathData([]);
      setSelectedPath([]);
      setChipData([]);
      setStepCounter(0);
      localStorage.setItem("coverage_area_id", "");
      localStorage.setItem("typeModal", "");
      return;
    }

    if (clickType === "modal") {
      setChipData((prev) => prev.slice(0, -1));
      const newLength = chipData.length - 1;

      if (newLength <= 0) {
        setCurrentParentId(null);
        setAreaDetails([]);
      } else if (newLength === 1) {
        setCurrentParentId(chipData[newLength - 1]?.id);
      } else {
        setCurrentParentId(chipData[newLength - 2]?.id);
      }

      setStepCounter((prev) => Math.max(0, prev - 1));
      setPathData(chipData.slice(0, -1));
    }
  };

  const resultArray = (value: any) => {
    if (stepCounter === 0) {
      setChipData([]);
      setPathData([]);
      setCurrentParentId(value?.area_id);
      handleSettingChipData(value);
      setStepCounter(1);
    } else {
      setCurrentParentId(value?.area_id);
      handleSettingChipData(value);
      setStepCounter((prev) => prev + 1);
    }
  };

  const handleCoverageAreaId = (value: any) => {
    if ("area_name" in value) {
      setChipData([]);
      handleSettingChipData(value);
      setSelectedRadioId(value);
      setPathData([value]);
    } else {
      setSelectedRadioId(value);
      setPathData((prev) => [...prev, value]);
    }
  };

  const handleRadioOnClick = (value: any) => {
    setCheckRadioRequestToSendarea_id("area_name" in value);
    resultArray(value);
  };

  return {
    selectedpath,
    areaModalOpen,
    handleAreaModalOpen,
    handleAreaModalClose,
    chipData,
    setChipData,
    provinceData,
    pathData,
    setPathData,
    areaDetails,
    stepCounter,
    setStepCounter,
    provinceLoading,
    selectedRadioId,
    setSelectedRadioId,
    detailsLoading,
    checkRadioRequestToSendarea_id,
    selectedAreaText,
    selectedAreaId,
    handleBack,
    handleCoverageAreaId,
    handleRadioOnClick,
  };
}
